/**
 * ProgramCard — learner grid tile for /learn/programs.
 *
 * An exact visual twin of the learner CourseCard (product rule: Course
 * Library and Learning Programs share one design) — cover, title + bucket
 * chip, description, progress bar with a courses-completed count, duration
 * caption. Only the data source differs.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import type { components } from '../../types/learnerApi';
import { useImageUrl } from '../../services/uploads';
import ProgressBar from './ProgressBar';

type Program = components['schemas']['Program'];

/** Per-learner progress block added by GET /me/programs. */
export interface ProgramProgress {
  courses_completed: number;
  courses_total: number;
  progress_percent: number;
  bucket: 'in_progress' | 'upcoming' | 'completed';
}

export type ProgramWithProgress = Program & { progress?: ProgramProgress };

type DisplayState = 'in_progress' | 'upcoming' | 'completed';

const bucketLabel: Record<DisplayState, string> = {
  in_progress: 'In Progress',
  upcoming: 'Upcoming',
  completed: 'Completed',
};

const bucketColor: Record<DisplayState, string> = {
  in_progress: 'text-secondary-600 dark:text-secondary-400 bg-secondary-50 dark:bg-secondary-950/40',
  upcoming: 'text-warning-600 dark:text-warning-400 bg-warning-50 dark:bg-warning-950/40',
  completed: 'text-text-secondary bg-text-tertiary/10',
};

interface ProgramCardProps {
  program: ProgramWithProgress;
}

const ProgramCard: React.FC<ProgramCardProps> = ({ program }) => {
  const [coverUrl] = useImageUrl(program.header_image ?? null);
  const progress = program.progress;
  const displayState: DisplayState = progress?.bucket ?? 'in_progress';
  const percent = progress?.progress_percent ?? 0;
  const completed = progress?.courses_completed ?? 0;
  const total = progress?.courses_total ?? 0;

  return (
    <Link
      to={`/learn/programs/${program.id}`}
      aria-label={program.name ?? undefined}
      className="group flex flex-col rounded-[28px] border-[7px] border-[rgba(189,188,201,0.24)] bg-surface-primary shadow-[7px_7px_14px_0px_rgba(0,0,0,0.2)] transition-shadow overflow-hidden"
    >
      {coverUrl ? (
        <img src={coverUrl} alt={program.name} className="w-full h-36 object-cover" />
      ) : (
        <div className="w-full h-36 bg-gradient-to-br from-primary-200 to-primary-400 dark:from-primary-800 dark:to-primary-600 flex items-center justify-center">
          <span className="text-3xl font-bold text-white/70">{program.name?.[0] ?? '?'}</span>
        </div>
      )}

      <div className="flex flex-col gap-2 p-4 flex-1">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-sm font-semibold text-text-primary line-clamp-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
            {program.name}
          </h3>
          <span
            className={`shrink-0 text-[11px] font-medium px-2 py-0.5 rounded-full ${bucketColor[displayState]}`}
          >
            {bucketLabel[displayState]}
          </span>
        </div>

        {program.description && (
          <p className="text-xs text-text-secondary line-clamp-1">{program.description}</p>
        )}

        <div className="mt-auto pt-2 space-y-1.5">
          <ProgressBar percent={percent} />
          <div className="flex items-center justify-between text-xs text-text-secondary">
            <span>
              {completed}/{total} courses
            </span>
            <span>{Math.round(percent)}%</span>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default ProgramCard;
